"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { capture } from "@/lib/analytics-client";
import {
  LOCALES,
  LOCALE_FLAGS,
  LOCALIZED_PATHS,
  localizedHref,
  readPreferredLocale,
  writePreferredLocale,
  type Locale,
} from "@/lib/i18n/locales";

const BANNER_COPY: Record<Locale, string> = {
  ko: "한국어로 보기",
  ja: "日本語で見る",
  id: "Lihat dalam Bahasa Indonesia",
  zh: "切换到简体中文",
  th: "ดูเป็นภาษาไทย",
  tl: "Tingnan sa Filipino",
  ms: "Lihat dalam Bahasa Melayu",
  vi: "Xem bằng tiếng Việt",
  es: "Ver en Español",
  pt: "Ver em Português",
};

const DISMISS_KEY = "audio2guitar.locale-banner-dismissed";

function detectSuggestedLocale(languages: readonly string[]): Locale | null {
  for (const raw of languages) {
    if (!raw) continue;
    const primary = raw.toLowerCase().split("-")[0];
    if (primary === "en") return null;
    for (const locale of LOCALES) {
      if (primary === locale) return locale;
    }
    if (primary === "fil") return "tl";
  }
  return null;
}

function isLocalePath(pathname: string): boolean {
  for (const locale of LOCALES) {
    if (pathname === `/${locale}` || pathname.startsWith(`/${locale}/`)) return true;
  }
  return false;
}

export function LocaleBanner() {
  const pathname = usePathname() || "/";
  const router = useRouter();
  const [suggestedLocale, setSuggestedLocale] = useState<Locale | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isLocalePath(pathname)) return;

    // Returning user with a saved locale preference on a localizable English
    // path: silently route them to their language. Skip auto-redirect when
    // the current path has no locale equivalent (blogs, /app, comparison
    // pages not yet translated) to avoid surprising redirects to the
    // homepage.
    const preferred = readPreferredLocale();
    if (preferred && LOCALIZED_PATHS.has(pathname)) {
      router.replace(localizedHref(preferred, pathname));
      return;
    }

    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    const langs = (navigator.languages && navigator.languages.length > 0)
      ? navigator.languages
      : [navigator.language || ""];
    const locale = detectSuggestedLocale(langs);
    if (!locale) return;

    setSuggestedLocale(locale);
    try {
      capture("locale_banner_shown", {
        suggested_locale: locale,
        pathname,
      });
    } catch {
      // PostHog may not be initialized yet; non-fatal.
    }
  }, [pathname, router]);

  if (!suggestedLocale) return null;

  const targetPath = localizedHref(suggestedLocale, pathname);

  const handleClick = () => {
    writePreferredLocale(suggestedLocale);
    try {
      capture("locale_banner_clicked", {
        suggested_locale: suggestedLocale,
        pathname,
        target: targetPath,
      });
    } catch {}
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {}
    try {
      capture("locale_banner_dismissed", {
        suggested_locale: suggestedLocale,
        pathname,
      });
    } catch {}
    setSuggestedLocale(null);
  };

  return (
    <div className="relative z-[60] bg-[#F59E0B] text-[#121110]">
      <div className="mx-auto max-w-6xl px-6 py-2 flex items-center justify-between gap-3 text-sm">
        <Link
          href={targetPath}
          onClick={handleClick}
          className="flex items-center gap-2 font-semibold hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#121110]"
        >
          <span aria-hidden="true">{LOCALE_FLAGS[suggestedLocale]}</span>
          <span>{BANNER_COPY[suggestedLocale]}</span>
          <span aria-hidden="true">→</span>
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="text-[#121110] opacity-70 transition hover:opacity-100"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
